import { CookieMonitor } from './CookieMonitor.js';

export type CookieManagerConfig = {
	userCookieName?: string,
	cookiePrefix?: string,
	checkInterval?: number,
	cookieExpires?: number,
	cookieTypes: Record<number, CookieType>,
	removeSubdomainCookies: boolean,
	debug: boolean,
	onAbsentCookie?: function,
	onBeforeInit?: function,
};

export type CookieType = {
	id: number,
	name: string,
};

/**
 * Класс для управления и мониторинга cookies на сайте.
 *
 * @class CookieManager
 * @description
 * Управляет состоянием cookie-файлов, отслеживает их изменения,
 * блокирует нежелательные cookie в соответствии с настройками пользователя.
 *
 * @param {CookieManagerConfig} config - Конфигурация менеджера cookie.
 * @param {string} [config.userCookieName='user_cookies'] - Имя cookie, хранящего настройки пользователя.
 * @param {string} [config.cookiePrefix='cookie_type-'] - Префикс для ключей типов cookie в настройках.
 * @param {number} [config.checkInterval=2000] - Интервал проверки изменений cookie (в миллисекундах).
 * @param {number} [config.cookieExpires=31536000] - Срок действия cookie в секундах (по умолчанию — 1 год).
 * @param {Object} [config.cookieTypes={}] - Объект с описанием типов cookie и списками связанных имён.
 * @param {Object} [config.removeSubdomainCookies=false] - Удалять ли куки со всех поддоменов? Напр. new.cookie.example.com -> .cookie.example.com -> .example.ru (по умолчанию - false).
 * @param {Function} [config.onAbsentCookie] - Колбэк, вызываемый при отсутствии cookie с настройками.
 * @param {Function} [config.onBeforeInit] - Колбэк, вызываемый перед инициализацией.
 *
 * @example
 * const manager = new CookieManager({
 *   userCookieName: 'user_preferences',
 *   cookieTypes: {
 *     1: [{ id: 10, name: 'tracking_cookie_1' }, { id: 11, name: 'tracking_cookie_2' }],
 *     2: [{ id: 25, name: 'ads_cookie' }]
 *   },
 *   onAbsentCookie: () => console.log('Настройки cookie не найдены'),
 *   onBeforeInit: (instance) => console.log('Инициализация начата', instance)
 * });
 */
export class CookieManager {
	constructor(config :CookieManagerOptions = {}) {
		this.config = {
			userCookieName: 'user_cookies',
			cookiePrefix: 'cookie_type-',
			checkInterval: 2000,
			cookieExpires: 60 * 60 * 24 * 30 * 12,
			cookieTypes: {},
			removeSubdomainCookies: false,
			debug: false,
			onAbsentCookie: () => this.config.debug && console.log('onAbsentCookie Event'),
			onBeforeInit: () => this.config.debug && console.log('onBeforeInit Event'),
			...config
		};

		this.monitor = null;
		this.needDisable = [];

		this.init();
	}

	init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setup());
		} else {
			this.setup();
		}
	}

	setup() {
		this.onBeforeInit();
		this.processInitialState();
	}

	onBeforeInit() {
		if (typeof this.onBeforeInit === 'function') {
			this.config.onBeforeInit(this);
		}
	}

	processInitialState() {
		const settingsCookie = this.getCookie(this.config.userCookieName);
		if (settingsCookie === undefined && typeof this.config.onAbsentCookie === 'function') {
			this.config.onAbsentCookie();
		} else {
			this.processCookies(settingsCookie);
		}
	}

	processCookies(settingsCookie) {
		try {
			const settings = JSON.parse(settingsCookie);
			this.needDisable = this.getCookiesToDisable(settings);
			if (this.needDisable.length > 0) {
				this.disableCookies(this.needDisable);
				this.startCookieMonitoring(this.needDisable);
			}
		} catch (error) {
			console.error('Error parsing cookie settings:', error);
		}
	}

	getCookiesToDisable(settings) {
		const needDisable = [];

		for (const type in settings) {
			if (settings[type] === false) {
				const typeId = parseInt(type.replace(this.config.cookiePrefix, ''));
				const cookieType = this.config.cookieTypes[typeId];
				if (cookieType && Array.isArray(cookieType)) {
					const cookies = cookieType.map(item => item.name);
					needDisable.push(...cookies);
				}
			}
		}

		return needDisable;
	}
	
	disableCookies(cookiesToDisable) {
		cookiesToDisable.forEach(cookieName => {
			this.removeCookie(cookieName);
		});
	}

	startCookieMonitoring(cookiesToDisable) {
		this.stopMonitoring();

		this.monitor = new CookieMonitor({
			checkInterval: this.config.checkInterval,
			onCookieChange: (cookieData) => {
				this.handleCookieChange(cookieData, cookiesToDisable);
			}
		});

		this.monitor.startMonitoring();
	}

	handleCookieChange(cookieData, cookiesToDisable) {
		if (cookiesToDisable.includes(cookieData.name)) {
			if (cookieData.action === 'change') {
				if (this.config.debug) {
					console.log(`Blocked cookie detected: ${cookieData.name}, removing...`);
				}
				this.removeCookie(cookieData.name);
			}
		}
	}

	getCookie(name) {
		if (typeof window.BX !== 'undefined' && window.BX.getCookie) {
			return window.BX.getCookie(name);
		}

		const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
		return match ? decodeURIComponent(match[2]) : undefined;
	}

	setCookie(name, value, options = {}) {
		if (typeof window.BX !== 'undefined' && window.BX.setCookie) {
			window.BX.setCookie(name, value, options);
		} else {
			let cookieString = `${name}=${encodeURIComponent(value)}`;

			if (options.expires) {
				const date = new Date();
				date.setTime(date.getTime() + (options.expires * 1000));
				cookieString += `; expires=${date.toUTCString()}`;
			}

			if (options.path) cookieString += `; path=${options.path}`;
			if (options.domain) cookieString += `; domain=${options.domain}`;
			if (options.secure) cookieString += '; secure';
			if (options.samesite) cookieString += `; samesite=${options.samesite}`;

			document.cookie = cookieString;
		}
	}
	
	removeCookie(name) {
		document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
		document.cookie = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
		document.cookie = name + '=; path=' + document.location.pathname + '; expires=Thu, 01 Jan 1970 00:00:00 GMT';

		if (this.config.removeSubdomainCookies) {
			this.getDomainHierarchy().forEach(domain => {
				document.cookie = name + '=; domain=' + domain + '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
				document.cookie = name + '=;path=/; domain=' + domain + '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
				document.cookie = name + '=; path=' + document.location.pathname + '; domain=' + domain + '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
			});
		}
	}

	getDomainHierarchy() {
		const domain = window.location.hostname;
		const variations = [];
		const parts = domain.split('.');

		if (parts.length < 2) return [domain];

		for (let i = 0; i <= parts.length - 2; i++) {
			variations.push('.' + parts.slice(i).join('.'));
		}

		return variations;
	}

	saveUserSettings(settings) {
		this.setCookie(
			this.config.userCookieName,
			JSON.stringify(settings),
			{ expires: this.config.cookieExpires, path: '/' }
		);
	}

	stopMonitoring() {
		if (this.monitor) {
			this.monitor.stopMonitoring();
			this.monitor = null;
		}
	}

	updateConfig(newConfig) {
		this.config = { ...this.config, ...newConfig };
	}

	updateCookieTypes(cookieTypes) {
		this.config.cookieTypes = cookieTypes;
	}

	getMonitoringMode() {
		return this.monitor ? this.monitor.getMode() : 'inactive';
	}

	destroy() {
		this.stopMonitoring();
	}
}
